import { useEffect, useRef } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { useTranslation } from 'react-i18next'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { useNotification } from 'hooks/notifications/useNotification'
import { Wrapper } from './styled'

interface IDownloadedImage {
  url: string
  data: string
}

interface IProps {
  content: string
  backgroundImage: File | string | null
  onGenerate: (pdf: jsPDF | null) => void
  preview?: boolean
}

const GeneratePdf = ({
  content,
  backgroundImage,
  onGenerate,
  preview = false,
}: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const element = useRef<HTMLDivElement>(null)

  const generate = async (html: HTMLDivElement) => {
    try {
      const pdf = new jsPDF('landscape', 'px', [1100, 852], true)
      if (preview) pdf.setProperties({ title: 'Preview' })
      const width = pdf.internal.pageSize.getWidth()
      const height = pdf.internal.pageSize.getHeight()

      if (backgroundImage) {
        let url = ''
        if (typeof backgroundImage === 'string') {
          url = backgroundImage
        } else {
          url = URL.createObjectURL(backgroundImage)
        }

        const baseUrl = url.split('?')[0]
        const imageCacheRaw = localStorage.getItem('bgImageCache') || '[]'
        const imageCache: IDownloadedImage[] = JSON.parse(imageCacheRaw)

        const cached = imageCache.find((item) => item.url === baseUrl)
        let bgImage: string | void

        if (cached) {
          bgImage = cached.data
        } else {
          bgImage = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache',
            },
          })
            .then((res) => res.blob())
            .then(
              (blob) =>
                new Promise<string>((resolve) => {
                  const reader = new FileReader()
                  reader.onloadend = () => resolve(reader.result as string)
                  reader.readAsDataURL(blob)
                })
            )
            .catch(() => {
              notification.warning(
                t('reusables.generatePdf.notifications.errorLoadingBgImage')
              )
            })
          if (bgImage) {
            imageCache.push({ url: baseUrl, data: bgImage })
            localStorage.setItem('bgImageCache', JSON.stringify(imageCache))
          }
        }
        if (bgImage) pdf.addImage(bgImage, 'JPEG', 0, 0, width, height)
      }

      const canvas = await html2canvas(html, {
        logging: true,
        imageTimeout: 1000,
        scale: 1.2,
        allowTaint: true,
        useCORS: true,
        backgroundColor: null,
      })
      const imgData = canvas.toDataURL('img/png')
      pdf.addImage(imgData, 'PNG', 0, 0, width, height)

      onGenerate(pdf)
    } catch (e) {
      onGenerate(null)
    }
  }

  useEffect(() => {
    const target = element.current
    if (!target) return

    const timeout = setTimeout(() => {
      if (target.innerHTML.trim()) {
        generate(target)
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [content])

  return (
    <Wrapper>
      <div
        ref={element}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    </Wrapper>
  )
}

export default GeneratePdf
